import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AvatarUpload } from '@/components/ui/avatar-upload'
import { updateOwnProfile, updateUserPassword } from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import type { Profile } from '@/types/domain'

type ProfileDialogProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    profile: Profile
}

export function ProfileDialog({ open, onOpenChange, profile }: ProfileDialogProps) {
    const queryClient = useQueryClient()
    const [firstName, setFirstName] = useState(profile.first_name ?? '')
    const [lastName, setLastName] = useState(profile.last_name ?? '')
    const [birthDate, setBirthDate] = useState(profile.birth_date ?? '')
    const [street, setStreet] = useState(profile.street ?? '')
    const [houseNumber, setHouseNumber] = useState(profile.house_number ?? '')
    const [postalCode, setPostalCode] = useState(profile.postal_code ?? '')
    const [city, setCity] = useState(profile.city ?? '')
    const [phoneNumber, setPhoneNumber] = useState(profile.phone_number ?? '')
    const [company, setCompany] = useState(profile.company ?? '')
    const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url ?? '')
    const [showPasswordChange, setShowPasswordChange] = useState(false)
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')

    const mutation = useMutation({
        mutationFn: () => {
            const normalizedFirstName = firstName.trim()
            const normalizedLastName = lastName.trim()

            if (!normalizedFirstName) {
                throw new Error('Bitte einen Vornamen eingeben.')
            }

            if (!normalizedLastName) {
                throw new Error('Bitte einen Nachnamen eingeben.')
            }

            return updateOwnProfile({
                first_name: normalizedFirstName,
                last_name: normalizedLastName,
                birth_date: birthDate || null,
                street: street || null,
                house_number: houseNumber || null,
                postal_code: postalCode || null,
                city: city || null,
                phone_number: phoneNumber || null,
                company: company || null,
                avatar_url: avatarUrl || null,
            })
        },
        onSuccess: () => {
            toast.success('Profil erfolgreich aktualisiert.')
            queryClient.invalidateQueries({ queryKey: queryKeys.currentUserProfile })
            onOpenChange(false)
        },
        onError: (error) => {
            toast.error(getErrorMessage(error))
        },
    })

    const passwordMutation = useMutation({
        mutationFn: async () => {
            if (!newPassword || newPassword.length < 6) {
                throw new Error('Das neue Passwort muss mindestens 6 Zeichen lang sein.')
            }

            if (newPassword !== confirmPassword) {
                throw new Error('Die Passwörter stimmen nicht überein.')
            }

            return updateUserPassword(newPassword)
        },
        onSuccess: () => {
            toast.success('Passwort erfolgreich geändert.')
            setNewPassword('')
            setConfirmPassword('')
            setShowPasswordChange(false)
        },
        onError: (error) => {
            toast.error(getErrorMessage(error))
        },
    })

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90dvh] flex-col sm:max-w-[560px]">
                <DialogHeader>
                    <DialogTitle>Mein Profil</DialogTitle>
                    <DialogDescription>
                        Hier kannst du deine persönlichen Daten verwalten.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto py-4 pr-1">
                    <AvatarUpload
                        userId={profile.id}
                        currentUrl={avatarUrl}
                        fullName={`${firstName ?? ''} ${lastName ?? ''}`.trim()}
                        onUploadComplete={setAvatarUrl}
                    />

                    <div className="grid gap-2">
                        <Label htmlFor="profile-email">E-Mail (nicht änderbar)</Label>
                        <Input id="profile-email" value={profile.email} disabled className="bg-muted" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="profile-first-name">Vorname *</Label>
                            <Input
                                id="profile-first-name"
                                required
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="profile-last-name">Nachname *</Label>
                            <Input
                                id="profile-last-name"
                                required
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="profile-company">Gutschein Code</Label>
                        <Input
                            id="profile-company"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="profile-birth-date">Geburtsdatum</Label>
                        <DatePickerInput
                            id="profile-birth-date"
                            value={birthDate}
                            onChange={setBirthDate}
                            placeholder="Geburtsdatum auswählen"
                        />
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                        <div className="col-span-3 grid gap-2">
                            <Label htmlFor="profile-street">Straße</Label>
                            <Input
                                id="profile-street"
                                value={street}
                                onChange={(e) => setStreet(e.target.value)}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="profile-house-number">Nr.</Label>
                            <Input
                                id="profile-house-number"
                                value={houseNumber}
                                onChange={(e) => setHouseNumber(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="profile-postal-code">PLZ</Label>
                            <Input
                                id="profile-postal-code"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                            />
                        </div>
                        <div className="col-span-3 grid gap-2">
                            <Label htmlFor="profile-city">Stadt</Label>
                            <Input
                                id="profile-city"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="profile-phone">Telefonnummer</Label>
                        <Input
                            id="profile-phone"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                        />
                    </div>

                    <div className="border-t border-border/50 pt-4 mt-2">
                        {!showPasswordChange ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-full sm:w-auto"
                                onClick={() => setShowPasswordChange(true)}
                            >
                                Passwort ändern
                            </Button>
                        ) : (
                            <div className="grid gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-medium text-sm">Passwort ändern</h4>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 px-2 text-muted-foreground hover:text-foreground"
                                        onClick={() => {
                                            setShowPasswordChange(false)
                                            setNewPassword('')
                                            setConfirmPassword('')
                                        }}
                                    >
                                        Abbrechen
                                    </Button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="grid gap-2">
                                        <Label htmlFor="new-password">Neues Passwort</Label>
                                        <Input
                                            id="new-password"
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="••••••••"
                                        />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label htmlFor="confirm-password">Wiederholen</Label>
                                        <Input
                                            id="confirm-password"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="••••••••"
                                        />
                                    </div>
                                </div>
                                <Button
                                    type="button"
                                    className="w-full sm:w-auto"
                                    onClick={() => passwordMutation.mutate()}
                                    disabled={passwordMutation.isPending}
                                >
                                    {passwordMutation.isPending ? 'Ändere Passwort...' : 'Passwort aktualisieren'}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        Abbrechen
                    </Button>
                    <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
                        {mutation.isPending ? 'Speichere ...' : 'Speichern'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
